import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Gift, MapPin, Truck, Loader2 } from 'lucide-react'
import { giftsApi, authApi, giftCategoriesApi, updateUserAccountHospitalName } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { usePageTitle } from '../../hooks/usePageTitle'

const UserExchange = () => {
  usePageTitle()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const [user, setUser] = useState<any>(null)
  const [gifts, setGifts] = useState<any[]>([])
  const [categories, setCategories] = useState<{ key: string; label: string }[]>([{ key: 'all', label: '全部' }])
  const [exchangeCount, setExchangeCount] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [showGiftStock, setShowGiftStock] = useState(true)
  const [allowExchange, setAllowExchange] = useState(true)
  const [loading, setLoading] = useState(true)

  // 优先从 userAccount 获取完整用户信息（含 hospitalName），与首页/活动页一致
  useEffect(() => {
    try {
      const stored = localStorage.getItem('userAccount')
      if (stored) {
        setUser(JSON.parse(stored))
      } else if (authUser) {
        setUser(authUser)
      }
    } catch {
      if (authUser) setUser(authUser)
    }
  }, [authUser])

  // 刷新用户信息（积分等）
  const refreshUser = async () => {
    const resp = await authApi.refreshUserInfo()
    if (resp && resp.status !== 'guest') {
      setUser(resp)
      setExchangeCount(resp.exchangeCount || 0)
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    // showGiftStock 单独存储
    const showStock = localStorage.getItem('showGiftStock')
    if (showStock !== null) setShowGiftStock(showStock === 'true')
    // allowExchange 单独存储
    const allowEx = localStorage.getItem('allowExchange')
    if (allowEx !== null) setAllowExchange(allowEx === 'true')

    const loadData = async () => {
      try {
        // 先获取用户机构信息，再加载礼品列表
        const userResp = await authApi.me()
        const userHospitalId = userResp?.hospital_id

        // 并行加载礼品列表、用户信息（驼峰格式）、分类数据
        const [giftsResp, , categoriesResp] = await Promise.all([
          giftsApi.list({ is_active: true, hospital_id: userHospitalId, page_size: 10000 }, true),
          authApi.refreshUserInfo(),  // 统一用 refreshUserInfo 获取驼峰格式
          giftCategoriesApi.list(),
        ]) as [any, any, any]

        const giftItems = giftsResp.gifts || giftsResp || []
        setGifts(giftItems.map((g: any) => ({
          ...g,
          deliveryType: g.delivery_type || 'both',
        })))

        // refreshUserInfo 已自动存储驼峰格式到 localStorage，无需重复处理
        if (userResp) {
          setUser(userResp)
          setExchangeCount(userResp.exchange_count || 0)
        }

        // 分类数据
        if (categoriesResp?.categories?.length) {
          const cats = [
            { key: 'all', label: '全部' },
            ...categoriesResp.categories.map((c: any) => ({ key: c.name, label: c.name }))
          ]
          setCategories(cats)
        }
      } catch (e) {
        console.error('Failed to load exchange data', e)
        // 降级：从 localStorage 读取
        const stored = localStorage.getItem('userAccount')
        if (stored) {
          try {
            const cached = JSON.parse(stored)
            setUser(cached)
            setExchangeCount(cached.exchangeCount || 0)
          } catch { /* ignore */ }
        }
      } finally {
        setLoading(false)
      }
    }
    loadData()
    updateUserAccountHospitalName()
  }, [])

  // 页面可见性变化时刷新用户信息（从后台切回来时更新积分）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUser()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const filteredGifts = gifts.filter((gift: any) => {
    if (!gift.enabled) return false
    if (selectedCategory !== 'all' && gift.category !== selectedCategory) return false
    return true
  })

  const userPoints = user?.points ?? 0
  const isLoggedIn = !!user

  return (
    <div className="min-h-fit bg-gray-50 pb-4">
      {/* 头部 */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 px-4 pt-6 pb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-green-100 text-sm">可兑换积分</p>
            <p className="text-4xl font-bold text-white mt-1">
              {loading ? <Loader2 className="w-8 h-8 animate-spin" /> : userPoints}
              <span className="text-lg text-green-200 ml-1">分</span>
            </p>
          </div>
          <Link to="/user/exchange-records" className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
            <p className="text-white/80 text-xs">历史兑换</p>
            <p className="text-white font-bold">{exchangeCount} 次</p>
          </Link>
        </div>
      </div>

      {/* 分类筛选 */}
      <div className="px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-md p-3">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button key={cat.key} onClick={() => setSelectedCategory(cat.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === cat.key ? 'bg-green-700 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}>
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 礼品列表 */}
      <div className="px-4 mt-4">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredGifts.map((gift) => {
              const hasEnoughPoints = userPoints >= (gift.points || 0)
              const hasStock = (gift.stock || 0) > 0
              const canExchange = allowExchange && isLoggedIn && hasEnoughPoints && hasStock
              const getStockText = () => {
                if (gift.stock === 0) return '已兑完'
                if (gift.stock < 5) return `仅剩${gift.stock}件`
                return ''
              }
              return (
                <div key={gift.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="relative aspect-square bg-gray-100">
                    {gift.image ? (
                      <img src={gift.image} alt={gift.name}
                        className="w-full h-full object-cover absolute inset-0"
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden') }} />
                    ) : null}
                    <div className={`absolute inset-0 flex items-center justify-center bg-gray-100 ${gift.image ? 'hidden' : ''}`}>
                      <Gift className="w-10 h-10 text-gray-300" />
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-white/90 backdrop-blur-sm rounded-full text-xs text-gray-700">
                        {gift.category || '其他'}
                      </span>
                    </div>
                    <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                      {gift.deliveryType === 'express' && (
                        <span className="px-2 py-0.5 bg-blue-500 text-white rounded-full text-xs flex items-center">
                          <Truck className="w-3 h-3 mr-0.5" /> 快递
                        </span>
                      )}
                      {gift.deliveryType === 'selfpickup' && (
                        <span className="px-2 py-0.5 bg-green-500 text-white rounded-full text-xs flex items-center">
                          <MapPin className="w-3 h-3 mr-0.5" /> 自提
                        </span>
                      )}
                      {gift.deliveryType === 'both' && (
                        <span className="px-2 py-0.5 bg-purple-500 text-white rounded-full text-xs">快递/自提</span>
                      )}
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-gray-800 text-sm truncate">{gift.name}</h3>
                    {gift.price > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">市场价: ¥{gift.price}</p>
                    )}
                    {showGiftStock && getStockText() && (
                      <p className={`text-xs mt-1 ${gift.stock === 0 ? 'text-red-500' : 'text-orange-500'}`}>
                        {getStockText()}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        <span className="text-orange-500 font-bold text-sm">{gift.points}积分</span>
                      </div>
                      <button onClick={() => navigate(`/user/gift/${gift.id}`)}
                        disabled={!canExchange}
                        className={`px-3 py-1 rounded-lg text-xs font-medium ${
                          canExchange ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}>
                        {!allowExchange ? '暂停兑换'
                          : !isLoggedIn ? '登录兑换'
                          : !hasStock ? '已兑完'
                          : !hasEnoughPoints ? '积分不足'
                          : '兑换'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {!loading && filteredGifts.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Gift className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>暂无可兑换礼品</p>
          </div>
        )}
      </div>

      {/* 底部版权 */}
      <div className="px-4 pb-4 text-center mt-4">
        <p className="text-xs text-gray-400">{localStorage.getItem('copyright') || '© 2026 眉山疾控'}</p>
      </div>
    </div>
  )
}

export default UserExchange
